/**
 * La sessione simulata: un telefono in più dentro lo stesso browser.
 *
 * Tutto lo stato di un dispositivo — identità, `deviceId`, profilo, coda di
 * invio, cursore del relay — vive dentro un solo database IndexedDB, e le
 * schede della stessa origine si parlano da un solo BroadcastChannel. Sono due
 * nomi: dare a quei due nomi un suffisso per-sessione basta a trasformare ogni
 * scheda, o ogni iframe, in un dispositivo indipendente. Il relay non se ne
 * accorge — vede N device distinti, esattamente come in produzione.
 *
 * L'identificativo arriva da `?sim=<id>` e, quando la query si perde, da
 * `window.name`: è l'unico contenitore che sopravvive a un reload e a una
 * riscrittura dell'URL senza appartenere all'origine (che invece è condivisa
 * fra tutte le sessioni).
 *
 * **In produzione non esiste**: `development` è `import.meta.env.DEV` al
 * chiamante reale, quindi il suffisso è la stringa vuota e i due nomi restano
 * identici a quelli di oggi, byte per byte.
 */

/** Minuscole, cifre e trattini: entra in un nome di database e in una query. */
const SIM_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

/** Prefisso del `window.name`, per non rubare il nome a una finestra altrui. */
export const SIM_WINDOW_NAME_PREFIX = "palestra-sim:";

export interface SimSessionSource {
  /** `import.meta.env.DEV` al punto di chiamata reale. */
  development: boolean;
  /** `location.search`. */
  search: string;
  /** `window.name`. */
  windowName: string;
}

/**
 * La query vince sul nome della finestra: chi naviga a `/?sim=b` da una scheda
 * che era `a` sta chiedendo di cambiare telefono, non di tornare al suo.
 */
export function resolveSimSessionId(source: SimSessionSource): string | undefined {
  if (!source.development) return undefined;
  const fromQuery = new URLSearchParams(source.search).get("sim");
  const fromName = source.windowName.startsWith(SIM_WINDOW_NAME_PREFIX)
    ? source.windowName.slice(SIM_WINDOW_NAME_PREFIX.length)
    : undefined;
  const candidate = fromQuery ?? fromName;
  return candidate && SIM_PATTERN.test(candidate) ? candidate : undefined;
}

/** Vero per un identificativo di sessione ben formato. */
export function isSimSessionId(value: string): boolean {
  return SIM_PATTERN.test(value);
}

let cached: string | undefined;
let resolved = false;

/**
 * L'identificativo della sessione corrente, deciso una volta sola per contesto
 * di navigazione: il nome del database non può cambiare a metà vita della
 * scheda.
 */
export function simSessionId(): string | undefined {
  // La guardia sta *prima* di tutto il resto perché in produzione sia una
  // costante: `import.meta.env.DEV` diventa `false` alla build, il corpo
  // diventa irraggiungibile e l'eliminazione del codice morto porta via con
  // sé il pattern, la lettura della query e il nome della finestra. Non è una
  // ripetizione di `resolveSimSessionId`: quella è la funzione pura, che
  // riceve `development` come dato per poter essere provata in entrambi i casi.
  if (!import.meta.env.DEV) return undefined;
  if (resolved) return cached;
  resolved = true;
  if (typeof window === "undefined") return undefined;
  cached = resolveSimSessionId({
    development: import.meta.env.DEV,
    search: window.location.search,
    windowName: window.name,
  });
  // Ancora la sessione alla scheda: sopravvive ai reload e agli URL riscritti.
  if (cached && !window.name.startsWith(SIM_WINDOW_NAME_PREFIX)) {
    window.name = `${SIM_WINDOW_NAME_PREFIX}${cached}`;
  }
  return cached;
}

/** Il suffisso da appendere ai nomi globali. Vuoto fuori dalla simulazione. */
export function simStorageSuffix(): string {
  if (!import.meta.env.DEV) return "";
  const id = simSessionId();
  return id ? `--sim-${id}` : "";
}

/**
 * Un percorso con la sessione riattaccata. Serve dove l'app riscrive l'URL:
 * un `pathname` nudo butterebbe via la sessione proprio durante i flussi —
 * rimozione del profilo, azzeramento del dispositivo, riscatto di un link di
 * invito — che il simulatore serve a provare.
 */
export function pathKeepingSim(pathname: string): string {
  if (!import.meta.env.DEV) return pathname;
  const id = simSessionId();
  return id ? `${pathname}?sim=${id}` : pathname;
}

/** Il percorso corrente, ripulito dalla query ma non dalla sessione. */
export function cleanPathKeepingSim(): string {
  return pathKeepingSim(window.location.pathname);
}

/** Il messaggio che un telefono simulato manda alla Regia che lo ospita. */
export const SIM_STATUS_MESSAGE = "palestra-sim-status";

export interface SimStatusMessage {
  type: typeof SIM_STATUS_MESSAGE;
  session: string;
  sync: string;
}

/**
 * La spia di stato del pannello: dentro un iframe di Regia, ogni battito della
 * connessione arriva al padre, che colora il bordo del telefono. Fuori dalla
 * simulazione — cioè sempre, in produzione — la funzione è un ramo morto che
 * la build elimina.
 */
export function reportSimSyncState(state: string): void {
  if (!import.meta.env.DEV) return;
  if (typeof window === "undefined" || window.parent === window) return;
  const session = simSessionId();
  if (!session) return;
  const message: SimStatusMessage = { type: SIM_STATUS_MESSAGE, session, sync: state };
  window.parent.postMessage(message, window.location.origin);
}
